/**
 * Sidebar config resolution.
 * Sets active state based on current URL path or controller path.
 *
 * Items can use either:
 * - `activeMatch` — predicate over the current URL path; for pages that a
 *   prefix can't express (e.g. a page living under another item's URL space).
 *   Outranks prefix matches on other items.
 * - `activeUrl` — matches against the current URL path (preferred, URL-based)
 * - `activeExact` — when true, activeUrl uses exact match instead of startsWith
 * - `activePath` — matches against store.currentPath (legacy, controller-based)
 *
 * An item may declare `children` (same shape, one level deep) — it renders as
 * a collapsible group whose parent link stays clickable. `expanded` on the
 * item sets the initial state on load (default collapsed); a group with an
 * active child always starts expanded and its parent is highlighted too.
 */

import { getFromStorage } from '@/helpers/storage'

/**
 * Initialize sidebar from provided config (legacy per-page mount path).
 * The router shell calls buildSidebar directly with the route path instead.
 *
 * @param {Object} store - Global store instance
 * @param {Object} config - Sidebar config with items array
 */
export default function initSidebar(store, config) {
  if (!config) return

  store.sidebar = buildSidebar(store, config, window.location.pathname)
}

// Single global preference: the sidebar is wide by default,
// the user can collapse it for the whole app.
export const SIDEBAR_VIEW_MODE_KEY = 'sidebar_view_mode'

/**
 * Pure resolver: filters items by visibility and marks the active one.
 *
 * @param {Object} store - Global store instance (visibility checks, currentPath)
 * @param {Object} config - Sidebar config with items array
 * @param {String} currentUrl - URL path to match items against
 * @returns {Object} - `{ compact, items }` ready for globalStore.sidebar
 */
export function buildSidebar(store, config, currentUrl) {
  const visible = (item) => !item.show || item.show(store)
  const filtered = config.items.filter(visible).map((item) => (item.children ?
    { ...item, children: item.children.filter(visible) } :
    item))

  // When several items match the current URL/path, pick the most specific one
  // (longest activeUrl/activePath). Otherwise on /scan_sessions/new both
  // "New Scan" and "All Sessions" would match and the order in the config wins.
  // An `activeMatch` predicate is an explicit claim and beats any prefix match.
  // Children compete in the same pool, so an active child outranks its parent.
  let best = null
  let bestLen = -1
  const consider = (item) => {
    if (!isItemActive(currentUrl, store.currentPath, item)) return
    const len = item.activeMatch ? Number.MAX_SAFE_INTEGER : (item.activeUrl || item.activePath || '').length
    if (len > bestLen) {
      bestLen = len
      best = item
    }
  }
  filtered.forEach((item) => {
    consider(item)
    item.children?.forEach(consider)
  })

  return {
    compact: Boolean(getFromStorage(SIDEBAR_VIEW_MODE_KEY, false)),
    items: filtered.map((item) => {
      const resolved = { ...item, active: item === best }
      if (item.children) {
        const hasActiveChild = item.children.includes(best)
        resolved.children = item.children.map((child) => ({ ...child, active: child === best }))
        // the parent stays highlighted alongside its active child so the
        // group still shows where the current page lives when collapsed
        resolved.active = resolved.active || hasActiveChild
        resolved.expanded = hasActiveChild || Boolean(item.expanded)
      }
      return resolved
    })
  }
}

function isItemActive(currentUrl, currentPath, item) {
  if (item.activeMatch) {
    return item.activeMatch(currentUrl)
  }

  if (item.activeUrl) {
    return item.activeExact ?
      currentUrl === item.activeUrl :
      currentUrl.startsWith(item.activeUrl)
  }

  if (item.activePath && currentPath) {
    return currentPath.startsWith(item.activePath)
  }

  return false
}
